/* eslint-disable prettier/prettier, curly */
/**
 * Rich Text Editor Component
 * MS365-style text editor with formatting toolbar
 */

import React, { useRef, useCallback, useState, useEffect } from 'react';
import {
  makeStyles,
  Button,
  Dropdown,
  Option,
  Divider,
} from '@fluentui/react-components';
import {
  TextBoldRegular,
  TextItalicRegular,
  TextUnderlineRegular,
  TextAlignLeftRegular,
  TextAlignCenterRegular,
  TextAlignRightRegular,
  TextBulletListRegular,
  TextNumberListLtrRegular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid #e1dfdd',
    borderRadius: '4px',
    backgroundColor: '#ffffff',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '8px 12px',
    borderBottom: '1px solid #e1dfdd',
    backgroundColor: '#fafafa',
    flexWrap: 'wrap',
  },
  toolbarGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  toolbarButton: {
    minWidth: '32px',
    height: '32px',
    padding: '4px 8px',
  },
  toolbarButtonActive: {
    backgroundColor: '#e6f7f5',
    color: '#008272',
  },
  dropdown: {
    minWidth: '120px',
  },
  fontSizeDropdown: {
    minWidth: '80px',
  },
  divider: {
    height: '24px',
    margin: '0 8px',
  },
  editor: {
    minHeight: '300px',
    padding: '16px',
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#323130',
    outline: 'none',
    overflowY: 'auto',
    fontFamily: 'Segoe UI, Arial, sans-serif',
    ':focus': {
      outline: 'none',
    },
  },
});

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: string;
}

const FONT_FAMILIES = [
  { label: 'Segoe UI', value: '"Segoe UI", sans-serif' },
  { label: 'Arial', value: 'Arial, sans-serif' },
  { label: 'Calibri', value: 'Calibri, sans-serif' },
  { label: 'Times New Roman', value: '"Times New Roman", serif' },
  { label: 'Courier New', value: '"Courier New", monospace' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Verdana', value: 'Verdana, sans-serif' },
];

const FONT_SIZES = [
  { label: '8', value: '8pt' },
  { label: '9', value: '9pt' },
  { label: '10', value: '10pt' },
  { label: '11', value: '11pt' },
  { label: '12', value: '12pt' },
  { label: '14', value: '14pt' },
  { label: '16', value: '16pt' },
  { label: '18', value: '18pt' },
  { label: '20', value: '20pt' },
  { label: '24', value: '24pt' },
  { label: '28', value: '28pt' },
  { label: '36', value: '36pt' },
];

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Type your message here...',
  minHeight = '300px',
}) => {
  const styles = useStyles();
  const editorRef = useRef<HTMLDivElement>(null);
  const [activeFormats, setActiveFormats] = useState<Set<string>>(new Set());
  const [currentFontFamily, setCurrentFontFamily] = useState<string>('"Segoe UI", sans-serif');
  const [currentFontSize, setCurrentFontSize] = useState<string>('12pt');

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const execCommand = useCallback((command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    updateActiveFormats();
  }, []);

  const updateActiveFormats = useCallback(() => {
    const formats = new Set<string>();
    if (document.queryCommandState('bold')) formats.add('bold');
    if (document.queryCommandState('italic')) formats.add('italic');
    if (document.queryCommandState('underline')) formats.add('underline');
    if (document.queryCommandState('insertUnorderedList')) formats.add('ul');
    if (document.queryCommandState('insertOrderedList')) formats.add('ol');
    if (document.queryCommandState('justifyLeft')) formats.add('left');
    if (document.queryCommandState('justifyCenter')) formats.add('center');
    if (document.queryCommandState('justifyRight')) formats.add('right');
    setActiveFormats(formats);
  }, []);

  const handleKeyUp = useCallback(() => {
    updateActiveFormats();
  }, [updateActiveFormats]);

  const handleMouseUp = useCallback(() => {
    updateActiveFormats();
  }, [updateActiveFormats]);

  const isActive = (format: string) => activeFormats.has(format);

  const handleFontFamilyChange = (_: any, data: any) => {
    const fontValue = data.optionValue;
    setCurrentFontFamily(fontValue);
    execCommand('fontName', fontValue);
  };

  const handleFontSizeChange = (_: any, data: any) => {
    const sizeValue = data.optionValue;
    setCurrentFontSize(sizeValue);
    const sizeMap: Record<string, string> = {
      '8pt': '1',
      '9pt': '1',
      '10pt': '2',
      '11pt': '2',
      '12pt': '3',
      '14pt': '4',
      '16pt': '4',
      '18pt': '5',
      '20pt': '5',
      '24pt': '6',
      '28pt': '6',
      '36pt': '7',
    };
    execCommand('fontSize', sizeMap[sizeValue] || '3');
  };

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.toolbarGroup}>
          <Dropdown
            className={styles.dropdown}
            placeholder="Font"
            value={FONT_FAMILIES.find(f => f.value === currentFontFamily)?.label || 'Segoe UI'}
            onOptionSelect={handleFontFamilyChange}
            size="small"
          >
            {FONT_FAMILIES.map(font => (
              <Option key={font.value} value={font.value}>
                {font.label}
              </Option>
            ))}
          </Dropdown>

          <Dropdown
            className={styles.fontSizeDropdown}
            placeholder="Size"
            value={FONT_SIZES.find(s => s.value === currentFontSize)?.label || '12'}
            onOptionSelect={handleFontSizeChange}
            size="small"
          >
            {FONT_SIZES.map(size => (
              <Option key={size.value} value={size.value}>
                {size.label}
              </Option>
            ))}
          </Dropdown>
        </div>

        <Divider vertical className={styles.divider} />

        <div className={styles.toolbarGroup}>
          <Button
            appearance="subtle"
            size="small"
            icon={<TextBoldRegular />}
            onClick={() => execCommand('bold')}
            className={isActive('bold') ? styles.toolbarButtonActive : ''}
            title="Bold (Ctrl+B)"
          />
          <Button
            appearance="subtle"
            size="small"
            icon={<TextItalicRegular />}
            onClick={() => execCommand('italic')}
            className={isActive('italic') ? styles.toolbarButtonActive : ''}
            title="Italic (Ctrl+I)"
          />
          <Button
            appearance="subtle"
            size="small"
            icon={<TextUnderlineRegular />}
            onClick={() => execCommand('underline')}
            className={isActive('underline') ? styles.toolbarButtonActive : ''}
            title="Underline (Ctrl+U)"
          />
        </div>

        <Divider vertical className={styles.divider} />

        <div className={styles.toolbarGroup}>
          <Button
            appearance="subtle"
            size="small"
            icon={<TextAlignLeftRegular />}
            onClick={() => execCommand('justifyLeft')}
            className={isActive('left') ? styles.toolbarButtonActive : ''}
            title="Align Left"
          />
          <Button
            appearance="subtle"
            size="small"
            icon={<TextAlignCenterRegular />}
            onClick={() => execCommand('justifyCenter')}
            className={isActive('center') ? styles.toolbarButtonActive : ''}
            title="Align Center"
          />
          <Button
            appearance="subtle"
            size="small"
            icon={<TextAlignRightRegular />}
            onClick={() => execCommand('justifyRight')}
            className={isActive('right') ? styles.toolbarButtonActive : ''}
            title="Align Right"
          />
        </div>

        <Divider vertical className={styles.divider} />

        <div className={styles.toolbarGroup}>
          <Button
            appearance="subtle"
            size="small"
            icon={<TextBulletListRegular />}
            onClick={() => execCommand('insertUnorderedList')}
            className={isActive('ul') ? styles.toolbarButtonActive : ''}
            title="Bullet List"
          />
          <Button
            appearance="subtle"
            size="small"
            icon={<TextNumberListLtrRegular />}
            onClick={() => execCommand('insertOrderedList')}
            className={isActive('ol') ? styles.toolbarButtonActive : ''}
            title="Numbered List"
          />
        </div>
      </div>

      <div
        ref={editorRef}
        className={styles.editor}
        contentEditable
        onInput={handleInput}
        onKeyUp={handleKeyUp}
        onMouseUp={handleMouseUp}
        data-placeholder={placeholder}
        style={{ minHeight }}
        suppressContentEditableWarning
      />
    </div>
  );
};

export default RichTextEditor;
